import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminOrderStatusBadge, AdminPaymentStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminPermissionGate } from '../../components/primitives/admin-permission-gate.js';
import { AdminApiError } from '../../api/admin-client.js';
import { formatPaise, formatDateTime } from '../../lib/format.js';
import { useAdminOrderDetail, useAdminOrderMutations } from '../hooks/use-orders.js';
import { StatusChangeDialog } from '../components/status-change-dialog.js';
import { CancelOrderDialog } from '../components/cancel-order-dialog.js';
import { InvoiceView } from '../components/invoice-view.js';
import { PackingSlipView } from '../components/packing-slip-view.js';
import { formatAddressLines } from '../lib/address-snapshot.js';

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-50"
      aria-label={`Copy ${value}`}
    >
      <Copy className="h-3 w-3" aria-hidden="true" />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function OrderDetailPage() {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { data, isLoading, isError, refetch } = useAdminOrderDetail(orderNumber);
  const { changeStatus, cancel } = useAdminOrderMutations();
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <AdminContent>
      <AdminPageHeader
        title={orderNumber ?? 'Order'}
        description="Order detail and operational actions."
        breadcrumbs={[{ label: 'Orders', to: ROUTE_NAMES.adminOrders }, { label: orderNumber ?? '' }]}
      />

      {actionError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      {isLoading && <AdminLoadingState label="Loading order…" />}
      {isError && (
        <AdminErrorState message="This order could not be loaded." onRetry={() => void refetch()} />
      )}

      {data && (
        <div className="space-y-4">
          <AdminSectionCard
            title="Order summary"
            actions={
              <AdminPermissionGate anyRole={['ADMIN']}>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowStatusDialog(true)}
                    disabled={data.order.validNextStatuses.filter((s) => s !== 'cancelled').length === 0}
                    className="rounded-full border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-40"
                  >
                    Change status
                  </button>
                  {data.order.validNextStatuses.includes('cancelled') && (
                    <button
                      type="button"
                      onClick={() => setShowCancelDialog(true)}
                      className="rounded-full border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      Cancel order
                    </button>
                  )}
                </div>
              </AdminPermissionGate>
            }
          >
            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd className="mt-1">
                  <AdminOrderStatusBadge status={data.order.status} />
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Payment</dt>
                <dd className="mt-1">
                  {data.order.paymentStatus ? (
                    <AdminPaymentStatusBadge status={data.order.paymentStatus} />
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Total</dt>
                <dd className="mt-1 font-medium text-gray-900">{formatPaise(data.order.total.amountPaise)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Placed</dt>
                <dd className="mt-1">{formatDateTime(data.order.createdAt)}</dd>
              </div>
            </dl>
          </AdminSectionCard>

          <AdminSectionCard title="Customer">
            <p className="text-sm font-medium text-gray-900">{data.order.customer.fullName ?? '—'}</p>
            <p className="flex items-center gap-2 text-sm text-gray-500">
              {data.order.customer.email}
              <CopyButton value={data.order.customer.email} />
            </p>
            <p className="mt-1 text-xs text-gray-400 capitalize">Account: {data.order.customer.accountStatus}</p>
          </AdminSectionCard>

          <AdminSectionCard title="Delivery address">
            {formatAddressLines(data.order.deliveryAddress).map((line, index) => (
              <p key={index} className="text-sm text-gray-700">
                {line}
              </p>
            ))}
          </AdminSectionCard>

          <AdminSectionCard title="Items">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="py-2 pr-4 font-medium">Item</th>
                  <th className="py-2 pr-4 font-medium">Qty</th>
                  <th className="py-2 pr-4 font-medium">Unit price</th>
                  <th className="py-2 pr-4 font-medium">Discount</th>
                  <th className="py-2 pr-4 font-medium">Tax</th>
                  <th className="py-2 pr-4 font-medium">Line total</th>
                </tr>
              </thead>
              <tbody>
                {data.order.items.map((item, index) => (
                  <tr key={index} className="border-b border-gray-50">
                    <td className="py-2 pr-4">
                      <p className="font-medium text-gray-900">{item.productName}</p>
                      <p className="text-xs text-gray-400">
                        {item.variantName} · SKU {item.sku}
                      </p>
                    </td>
                    <td className="py-2 pr-4">{item.quantity}</td>
                    <td className="py-2 pr-4">{formatPaise(item.unitPrice.amountPaise)}</td>
                    <td className="py-2 pr-4">{formatPaise(item.lineDiscount.amountPaise)}</td>
                    <td className="py-2 pr-4">{formatPaise(item.lineTax.amountPaise)}</td>
                    <td className="py-2 pr-4">{formatPaise(item.lineTotal.amountPaise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminSectionCard>

          <AdminSectionCard title="Pricing">
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-gray-500">Subtotal</dt>
                <dd>{formatPaise(data.order.subtotal.amountPaise)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Discount</dt>
                <dd>-{formatPaise(data.order.discount.amountPaise)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Tax</dt>
                <dd>{formatPaise(data.order.tax.amountPaise)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Delivery fee</dt>
                <dd>{formatPaise(data.order.deliveryFee.amountPaise)}</dd>
              </div>
            </dl>
            {data.order.coupon && (
              <p className="mt-3 text-sm text-gray-600">
                Coupon <span className="font-medium">{data.order.coupon.code}</span> applied:{' '}
                {formatPaise(data.order.coupon.discountApplied.amountPaise)} off
              </p>
            )}
          </AdminSectionCard>

          <AdminSectionCard title="Payment">
            {data.order.payment ? (
              <div className="space-y-3 text-sm">
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div>
                    <dt className="text-gray-500">Method</dt>
                    <dd className="capitalize">{data.order.payment.detail.method}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Status</dt>
                    <dd>
                      <AdminPaymentStatusBadge status={data.order.payment.detail.status} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Amount</dt>
                    <dd>{formatPaise(data.order.payment.detail.amountPaise)}</dd>
                  </div>
                  {data.order.payment.detail.razorpayPaymentId && (
                    <div>
                      <dt className="text-gray-500">Razorpay payment</dt>
                      <dd className="flex items-center gap-2">
                        <span className="font-mono text-xs">{data.order.payment.detail.razorpayPaymentId}</span>
                        <CopyButton value={data.order.payment.detail.razorpayPaymentId} />
                      </dd>
                    </div>
                  )}
                </dl>
                {data.order.payment.attempts.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                      Attempt history
                    </p>
                    <ul className="space-y-1">
                      {data.order.payment.attempts.map((attempt) => (
                        <li key={attempt.id} className="text-xs text-gray-600">
                          #{attempt.attemptNumber} — {attempt.status}
                          {attempt.errorDescription ? ` (${attempt.errorDescription})` : ''} ·{' '}
                          {formatDateTime(attempt.attemptedAt)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No payment record for this order.</p>
            )}
          </AdminSectionCard>

          <AdminSectionCard title="Refunds">
            {data.order.payment && data.order.payment.refunds.length > 0 ? (
              <ul className="space-y-2">
                {data.order.payment.refunds.map((refund) => (
                  <li key={refund.id} className="flex items-center justify-between text-sm">
                    <span>
                      {formatPaise(refund.amountPaise)} — {refund.status}
                      {refund.reason ? ` (${refund.reason})` : ''}
                    </span>
                    <span className="text-xs text-gray-400">{formatDateTime(refund.initiatedAt)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">
                No refunds yet.{' '}
                {data.order.payment && (
                  <Link
                    to={`${ROUTE_NAMES.adminPayments}/${data.order.payment.detail.id}`}
                    className="text-brand-foreground underline"
                  >
                    Go to payment to initiate one
                  </Link>
                )}
              </p>
            )}
          </AdminSectionCard>

          <AdminSectionCard title="Timeline">
            <ol className="space-y-3">
              {data.order.statusHistory.map((entry, index) => (
                <li key={index} className="flex items-start gap-3 text-sm">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gray-300" aria-hidden="true" />
                  <div>
                    <p className="text-gray-900">
                      {entry.fromStatus ? `${entry.fromStatus.replace(/_/g, ' ')} → ` : ''}
                      <span className="font-medium capitalize">{entry.toStatus.replace(/_/g, ' ')}</span>
                    </p>
                    {entry.note && <p className="text-xs text-gray-500">{entry.note}</p>}
                    <p className="text-xs text-gray-400">{formatDateTime(entry.changedAt)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </AdminSectionCard>

          <AdminSectionCard title="Invoice / Packing slip">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Invoice</h3>
                <InvoiceView invoice={data.invoice} />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Packing slip</h3>
                <PackingSlipView packingSlip={data.packingSlip} />
              </div>
            </div>
          </AdminSectionCard>
        </div>
      )}

      {showStatusDialog && data && (
        <StatusChangeDialog
          orderNumber={data.order.orderNumber}
          validNextStatuses={data.order.validNextStatuses}
          isSubmitting={changeStatus.isPending}
          onClose={() => setShowStatusDialog(false)}
          onSubmit={(input) => {
            setActionError(null);
            changeStatus.mutate(
              { orderNumber: data.order.orderNumber, input },
              {
                onSuccess: () => setShowStatusDialog(false),
                onError: (error) =>
                  setActionError(error instanceof AdminApiError ? error.message : 'Could not change status.'),
              },
            );
          }}
        />
      )}

      {showCancelDialog && data && (
        <CancelOrderDialog
          orderNumber={data.order.orderNumber}
          isSubmitting={cancel.isPending}
          onClose={() => setShowCancelDialog(false)}
          onSubmit={(input) => {
            setActionError(null);
            cancel.mutate(
              { orderNumber: data.order.orderNumber, input },
              {
                onSuccess: () => setShowCancelDialog(false),
                onError: (error) =>
                  setActionError(error instanceof AdminApiError ? error.message : 'Could not cancel order.'),
              },
            );
          }}
        />
      )}
    </AdminContent>
  );
}
